import AppData from "./app.data";
import { ApiClient } from "../../utils/apiClient";
import { useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import Main3DManager from "./main.3d.manager";
import { Slot } from "@/types/slotsData";
import { Artwork } from "@/types/artworksData";
import useEvents from "../../hooks/useEvents";
import { WEBGL } from "./utils/webGL.utils";
import { BuyArtworkModal } from "../modals/buy.artwork";
import { resolveActiveMoodIndex } from "../../utils/moodVisibility";
import { resolveArtworkViewerMedia } from "./media.viewer.utils";
import type { SpatialMigrationGalleryPreviewData } from "../../pages/admin/spatial-migration-preview.adapter";

const appData = new AppData()

const BASE_API = process.env.REACT_APP_BASE_API;
const apiClient = ApiClient(BASE_API);

export type PreviewGalleryProps = {
    initialData?: SpatialMigrationGalleryPreviewData;
    interactive?: boolean;
    containerClassName?: string;
    onLoadReady?: () => void;
    onLoadError?: (message: string) => void;
};

export const PreviewGallery = ({
    initialData,
    interactive = true,
    containerClassName = "",
    onLoadReady,
    onLoadError,
}: PreviewGalleryProps = {}) => {
    const [searchParams] = useSearchParams()
    const provider = searchParams.get("provider")
    const collectionId = searchParams.get("collectionId")

    const [selectedSlot, setSelectedSlot] = useState<string>();
    const [selectedArtwork, setSelectedArtwork] = useState<Artwork>(null);
    const [buyArtworkModal, setBuyArtworkModal] = useState<boolean>(false);

    const canvasRef = useRef(null);
    const [main3DManager, setMain3DManager] = useState<
        Main3DManager | undefined
    >();

    const onArtworkClicked = useCallback((slot: Slot) => {
        if (!interactive || !slot?.artworkWrapper) return;
        if (main3DManager?.isVRPresenting?.() && slot.artworkWrapper) {
            const activeArtwork = slot.artworkWrapper.artworks[slot.artworkWrapper.activeArtwork];
            if (activeArtwork && (activeArtwork.type === "image" || activeArtwork.type === "video" || activeArtwork.type === "3dmodel")) {
                void resolveArtworkViewerMedia(activeArtwork, "preview", {
                    apiClient,
                    baseAPI: BASE_API,
                    artworkWrapper: slot.artworkWrapper,
                    spaceData: appData.spaceData,
                    mediaDelivery: main3DManager.getRuntimeSnapshot()?.mediaDelivery || null,
                }).then((resolvedMedia) => {
                    if (resolvedMedia) {
                        return main3DManager.openVRViewer(resolvedMedia);
                    }
                    console.warn("PreviewGallery: unable to resolve VR viewer media for artwork", activeArtwork?._id);
                    return null;
                }).catch((error) => {
                    console.error("PreviewGallery: failed to resolve artwork media for VR viewer", error);
                });
                return;
            }
        }

        setSelectedSlot(slot.artworkWrapper.slotTarget.toString());
          const activeArtwork = slot.artworkWrapper.artworks[slot.artworkWrapper.activeArtwork];
          const artworkWithWrapperId = (activeArtwork && slot.artworkWrapper?._id && (activeArtwork.type === "image" || activeArtwork.type === "video"))
            ? {
                ...activeArtwork,
                artworkData: {
                  ...activeArtwork.artworkData,
                  frame: {
                    ...activeArtwork.artworkData?.frame,
                    artworkWrapperID: activeArtwork.artworkData?.frame?.artworkWrapperID ?? slot.artworkWrapper._id
                  }
                }
              }
            : activeArtwork;
          setSelectedArtwork(artworkWithWrapperId)
          setBuyArtworkModal(true);
        main3DManager.setKeyAvailability(false);
    }, [interactive, main3DManager]);

    useEffect(() => {
        let _main3D = new Main3DManager();
        appData.setMain3DManager(_main3D);
        setMain3DManager(_main3D);
        return () => {
            _main3D.destroy();
            appData.setMain3DManager(null);
        }
    }, [])

    useEffect(() => {
        if (main3DManager && canvasRef.current) {
          main3DManager.init(canvasRef.current);
          main3DManager.setArtworkClickEvent(onArtworkClicked);
        }
      }, [main3DManager, canvasRef]);

    useEffect(() => {
        if (main3DManager) {
          main3DManager.setVRFailureEvent((reason) => {
            if (reason) {
              console.warn("PreviewGallery VR failure:", reason);
            }
          });
        }
      }, [main3DManager]);

    useEffect(() => {
        let active = true;
        if (main3DManager && WEBGL.isWebGLAvailable()) {
            const init3D = async () => {
                try {
                    const galleryPreviewData = initialData
                        ? { status: true, data: initialData }
                        : await apiClient.getGalleryPreview(provider,collectionId)
                    if (!active) return;
                    if (galleryPreviewData.status) {
                        // set space, gallery data
                        const spaceData = galleryPreviewData.data.space;
                        const galleryData = galleryPreviewData.data.gallery;
                        const fallbackMood =
                            Number.isInteger(galleryData?.data?.mood) ? galleryData.data.mood : undefined;
                        const resolvedMood = resolveActiveMoodIndex(
                            spaceData?.data?.moods,
                            fallbackMood,
                            {
                                galleryId: galleryData?._id
                            }
                        );
                        if (!galleryData.data) {
                            galleryData.data = {} as any;
                        }
                        galleryData.data.mood = resolvedMood;

                        appData.setSpaceData(spaceData);
                        appData.setGalleryData({
                            ...galleryData,
                            artworkWrappers: galleryPreviewData.data.wrappers,
                            editable: false
                        });
                        await main3DManager.loadGallery({ startBackgroundSound: !initialData });
                        if (!active) return;
                        main3DManager.setKeyAvailability(true);
                        onLoadReady?.();
                    }
                } catch (error) {
                    if (!active) return;
                    console.error("PreviewGallery: Error initializing 3D scene", error);
                    onLoadError?.(error instanceof Error ? error.message : "3D preview failed to load");
                }
            }
            init3D()
        }

        return () => {
            active = false;
            if (main3DManager) {
                main3DManager.dispose();
            }
        }
    }, [provider, collectionId, initialData, main3DManager, onLoadError, onLoadReady])

    useEvents(main3DManager, canvasRef, []);

    return (
        <div className={`position-relative ${containerClassName}`.trim()} aria-label="3D gallery preview">
            <div id="WebGL" draggable="false" ref={canvasRef}></div>
            <div id="loading" className="loading-screen">Loading ...</div>
            <div id="joystick-background"></div>
            <div id="joystick-thumb"></div>
            {
            interactive && buyArtworkModal && <BuyArtworkModal
                onBuyHandler={() => { }}
                artwork={selectedArtwork}
                onHide={() => {
                    setBuyArtworkModal(false);
                    main3DManager.setKeyAvailability(true);
                }}
                galleryId={collectionId}
                show={buyArtworkModal}
                slot={Number(selectedSlot)}
                onEnquireHandler={() => { }}
                successNotify={() => { }}
                failedNotify={() => { }}
                />
            }
        </div>
    )
}

export default PreviewGallery;
